/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Shared motion token for "active / running" STATUS icons (e.g. an ArrowPathIcon
 * on a job/agent that is currently running). These are NOT data-loading spinners —
 * do not use <Spinner> for them. Apply this token plus a chariot tone token to the
 * existing status icon so it spins reduce-motion-safely:
 *   <ArrowPathIcon className={cn('size-4 text-chariot-status-medium', SPINNER_MOTION)} />
 */
export const SPINNER_MOTION = 'motion-safe:animate-spin';

const spinnerVariants = cva('shrink-0 motion-safe:animate-spin', {
  variants: {
    size: {
      xs: 'size-3',
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-8',
      xl: 'size-12',
    },
    tone: {
      default: 'text-chariot-stroke',
      brand: 'text-chariot-brand-primary',
      muted: 'text-chariot-text-secondary',
      inverse: 'text-white',
    },
  },
  defaultVariants: {
    size: 'md',
    tone: 'muted',
  },
});

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  /** Wrap in a full-size flex container that centers the spinner. */
  centered?: boolean;
  /** Screen-reader label. Defaults to "Loading…". */
  label?: string;
}

/**
 * Canonical data-loading spinner. Renders the standard dashed-arc spinner via the
 * reduce-motion-safe `motion-safe:animate-spin` token. Defaults `data-testid="loader"`
 * for compatibility with existing tests. For an "active/running" status icon use the
 * `SPINNER_MOTION` token on the icon instead — NOT this component.
 */
const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size, tone, centered, label = 'Loading…', className, ...props }, ref) => (
    <div
      ref={ref}
      role="status"
      data-testid="loader"
      className={cn(
        centered ? 'flex size-full items-center justify-center' : 'inline-flex',
        className
      )}
      {...props}
    >
      <svg
        viewBox="0 0 50 50"
        fill="none"
        aria-hidden="true"
        className={cn(spinnerVariants({ size, tone }))}
      >
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeDasharray="97.6 28"
          strokeDashoffset="0"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  )
);
Spinner.displayName = 'Spinner';

export { Spinner, spinnerVariants };
